/**
 * Sprint 7 — Avatar avec initiales auto-colorées (pattern Float §3.2).
 *
 * Props :
 *  - person : { firstName, lastName, avatarUrl?, id? } OU { fullName, initials?, avatarUrl?, id? }
 *  - size   : taille en pixels (défaut 32)
 *  - title  : title HTML / tooltip (sinon = fullName)
 *
 * Si `avatarUrl` est présent → <img>, sinon cercle de couleur stable avec
 * les initiales blanches (couleur dérivée d'un hash simple du nom pour
 * rester stable d'une session à l'autre).
 */

const PALETTE = [
  '#1976D2',
  '#388E3C',
  '#D32F2F',
  '#7B1FA2',
  '#F57C00',
  '#0097A7',
  '#5D4037',
  '#455A64',
  '#C2185B',
  '#00796B',
];

function hashString(s) {
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function buildInitials(person) {
  if (person.initials) return person.initials;
  const first = person.firstName?.[0] ?? '';
  const last = person.lastName?.[0] ?? '';
  const combined = (first + last).toUpperCase();
  if (combined) return combined;
  const full = person.fullName ?? '';
  const parts = full.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function buildFullName(person) {
  if (person.fullName) return person.fullName;
  return [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
}

export default function Avatar({ person, size = 32, title }) {
  if (!person) return null;
  const fullName = buildFullName(person);
  const initials = buildInitials(person);
  const seed = person.id ?? fullName ?? initials;
  const color = PALETTE[hashString(String(seed)) % PALETTE.length];
  const fontSize = Math.max(10, Math.round(size * 0.4));

  if (person.avatarUrl) {
    // P1 — Cache-bust au rendu, pas en BDD : on persiste l'URL nue (clé S3
    // déterministe `avatars/<personId>.<ext>`). On affiche donc l'URL telle
    // quelle. Compromis V1 assumé : après écrasement d'un avatar, le cache
    // navigateur peut rester périmé un temps (même clé) — on préfère ça à une
    // URL polluée par un `?t=` aléatoire (qui casserait le cache à chaque
    // render et empêcherait toute mise en cache). Si un jour l'objet Person
    // porte un `updatedAt`, on pourrait y baser un cache-bust stable.
    return (
      <img
        src={person.avatarUrl}
        alt={fullName || initials}
        title={title ?? fullName}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <span
      title={title ?? fullName}
      aria-label={fullName || initials}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        fontWeight: 600,
        letterSpacing: 0.5,
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}
